import * as React from "react";
import styled from "@emotion/styled";
import {
  AbsoluteLink,
  Avatar,
  EntityBadge,
  H3,
  IconEnvelopeHeart,
  ScreenReaderText,
  Stack,
  Text,
  Tooltip,
} from "atoms";
import { FollowButton } from "features/follows";
import { Image, Thumbnail } from "features/images";
import placeholderAvatar from "assets/images/default-avatar.svg";
import featuredProjectIcon from "atoms/icons/featured-project.svg";

import { Artist, ReviewableType } from "../types";

interface Props {
  id: string;
  name?: string;
  type: ReviewableType;
  heroImage: Image;
  reviewCount?: number;
  acceptsCommissions?: boolean;
  artistCollections?: Artist["artistCollections"];
  isSmall?: boolean;
  showFollowButton?: boolean;
}

function ReviewableCard({
  id,
  name,
  type,
  heroImage,
  reviewCount,
  artistCollections,
  acceptsCommissions = false,
  isSmall = false,
  showFollowButton = true,
  ...props
}: Props) {
  const [isSquarish, setIsSquarish] = React.useState(true);
  const handleImgLoad: React.ReactEventHandler<HTMLImageElement> = (event) => {
    const { naturalWidth, naturalHeight } = event.currentTarget;
    setIsSquarish(Math.abs(naturalWidth / naturalHeight - 1) <= 0.1);
  };

  const isArtist = type === "ARTIST";

  const entityURL = `/${type.toLowerCase()}s/${id}`;
  const firstArtistImage =
    isArtist && !!artistCollections?.[0]?.collectionImage
      ? artistCollections[0]?.collectionImage
      : undefined;

  return (
    <Card {...props} className="ProjectCard">
      {!isSmall && (
        <CardImageWrapper>
          {!isSquarish && (
            <CardImageBackground
              src={heroImage}
              alt=""
              aria-hidden="true"
              entityType={type}
            />
          )}
          <CardImage
            onLoad={handleImgLoad}
            src={firstArtistImage ?? heroImage}
            alt={`Featured NFT from ${name} collection`}
            isSquarish={isSquarish}
            loading="lazy"
          />
        </CardImageWrapper>
      )}

      <AbsoluteLink to={entityURL}>
        <ScreenReaderText>{name}</ScreenReaderText>
      </AbsoluteLink>

      <CardHeader>
        {isArtist && (
          <CardAvatar>
            <Avatar
              src={heroImage?.url150 ?? heroImage?.url ?? placeholderAvatar}
              size={40}
            />
          </CardAvatar>
        )}
        <Stack gap="xxs">
          <CardTitle>
            <SH3 size="xs" aria-hidden>
              {name}
            </SH3>
          </CardTitle>

          <Interactions>
            {reviewCount !== undefined && (
              <Reviews>
                {reviewCount} Review{reviewCount !== 1 ? "s" : ""}
              </Reviews>
            )}
            {isArtist ? (
              <>
                {showFollowButton && (
                  <SFollowButton
                    unfollowText={
                      isSmall && showFollowButton ? "Unfollow" : "Following"
                    }
                    small
                    entityId={id}
                  />
                )}
                {acceptsCommissions && (
                  <CommissionsTooltip
                    aria-label="This artist accepts commissions"
                    label={<IconEnvelopeHeart primary="fg" accent="accent4" />}
                    positionY="top"
                    positionX="left"
                    allowsHover
                  >
                    <Text size="xxs">
                      This artist accepts
                      <br />
                      commission requests
                    </Text>
                  </CommissionsTooltip>
                )}
              </>
            ) : (
              <ReviewableTypeLabel type={type} />
            )}
          </Interactions>
        </Stack>
      </CardHeader>
    </Card>
  );
}

const Card = styled.article(({ theme }) => ({
  background: theme.colors.bgLight,
  borderRadius: theme.borderRadius.sm,
  boxShadow: theme.boxShadow.light,
  overflow: "hidden",
  position: "relative",
}));

const CardHeader = styled.div({
  display: "flex",
  flex: 1,
  gap: "3.33%",
  alignItems: "center",
  justifyContent: "stretch",
  padding: "4% 5%",
  position: "relative",
  "&>div:last-of-type": {
    flex: 1,
  },
});

const CardAvatar = styled.div({
  flex: "0 0 40px",
  display: "flex",
  alignItems: "start",
  alignSelf: "start",
});

const CardTitle = styled.header(({ theme }) => ({
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "nowrap",
  gap: 5,
  display: "flex",
  flex: 1,
  fontSize: theme.fontSizes.xs,
  paddingTop: ".1em",
  "&:only-child": {
    alignItems: "flex-start",
  },
}));
const SH3 = styled(H3)(({ theme }) => ({
  ...theme.fonts.display,
  alignItems: "center",
  display: "flex",
  lineHeight: "1.1",
  margin: 0,
}));

const CommissionsTooltip = styled(Tooltip)(({ theme }) => ({
  display: "block",
  position: "static",
  zIndex: 10,
  "&>button": {
    backgroundColor: theme.colors.bg,
    borderRadius: theme.borderRadius.round,
    boxShadow: theme.boxShadow.light,
    position: "absolute",
    transform: "translate(-35%, 0%)",
    left: "5%",
    top: 35,
    display: "grid",
    placeItems: "center",
    width: 24,
    height: 24,
    svg: {
      width: 13,
      height: "auto",
      display: "block",
    },
  },
  "&>button::after": {
    position: "absolute",
    zIndex: 600,
  },
  ".TooltipContent": {
    boxShadow: "none",
    textAlign: "center",
    padding: 10,
    left: 10,
    right: 10,
    bottom: 10,
    maxWidth: "calc(100% - 20px)",
    minWidth: 0,
    width: "calc(100% - 20px)",
    "&::after, &::before": {
      content: "none",
    },
    "&>button+div>*:first-of-type": {
      margin: 0,
      div: {
        justifyContent: "center",
        a: {
          flex: "1 0 auto",
          margin: "0 !important",
        },
      },
    },
  },
}));

const CardImageWrapper = styled("figure", {
  shouldForwardProp: (prop) => prop !== "isFeatured",
})<{ isFeatured?: boolean }>(({ isFeatured = false }) => ({
  alignItems: "center",
  aspectRatio: "1",
  display: "flex",
  flex: "0 0 auto",
  justifyContent: "center",
  margin: 0,
  overflow: "hidden",
  position: "relative",
  "@media (prefers-reduced-motion: no-preference)": {
    transition: "opacity 0.1s ease",
  },
  "&:hover": {
    opacity: 0.7,
  },
  ...(isFeatured
    ? {
        "&::after": {
          content: "''",
          backgroundImage: `url(${featuredProjectIcon})`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "top right",
          backgroundSize: "contain",
          display: "block",
          height: "72px",
          pointerEvents: "none",
          position: "absolute",
          right: "10px",
          top: "10px",
          width: "130px",
          maxWidth: "33%",
          minWidth: "71px",
          zIndex: 3,
        },
      }
    : {}),
}));

const CardImage = styled(Thumbnail)<{ isSquarish: boolean }>(
  ({ isSquarish, theme }) => ({
    zIndex: "2",
    "&:before, &>img": {
      objectFit: isSquarish ? "cover" : "contain",
      borderRadius: theme.borderRadius.sm,
    },
  })
);

const CardImageBackground = styled(Thumbnail)({
  bottom: "-10px",
  left: "-10px",
  opacity: ".33",
  position: "absolute",
  top: "-10px",
  width: "calc(100% + 20px)",
  height: "calc(100% + 20px)",
  right: "-10px",
  zIndex: "1",
  filter: "blur(10px)",
  objectFit: "cover",
});

const Interactions = styled.div({
  flex: 1,
  alignItems: "center",
  display: "flex",
  flexWrap: "wrap",
  // gap: "1px 10px",
  gap: "1px 0",
  justifyContent: "space-between",
});

const Reviews = styled.div(({ theme }) => ({
  color: theme.colors.fg70,
  flex: 1,
  fontSize: 12,
  lineHeight: 1.3,
  marginRight: 10,
  whiteSpace: "nowrap",
}));

const SFollowButton = styled(FollowButton)(({ theme }) => ({
  position: "unset",
  '&[type="button"]': {
    position: "relative",
    zIndex: 10,
  },
  ".Tooltip": {
    position: "static",
  },
  ".TooltipContent": {
    backgroundColor: theme.colors.bg,
    boxShadow: "none",
    left: 10,
    right: 10,
    bottom: 10,
    maxWidth: "calc(100% - 20px)",
    minWidth: 0,
    padding: "30px 10px 10px 10px",
    textAlign: "center",
    transform: "none",
    zIndex: 600,

    "&::before, &::after": {
      content: "none",
    },
    "div:last-of-type": {
      justifyContent: "stretch",
      a: { width: "100%" },
    },
  },
  "&>button": {
    position: "relative",
    zIndex: 10,
  },
  "button::before, button::after": {
    content: "none",
  },
}));

interface ReviewableTypeLabelProps {
  type: string;
}

function ReviewableTypeLabel({ type }: ReviewableTypeLabelProps) {
  return <EntityBadge entityType={type} />;
}

export { ReviewableCard };
